import { useState } from 'react';
import { Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import { Drawers } from 'app/types/drawer';
import { FieldProps, DataForm, FieldType } from 'components';
import { ToolMaintenance } from 'app/types/tool';
import { ToolFilterState } from 'constants/tool';
import moment from 'moment';
import { formats } from 'utils/datetime';
import { useSelector } from 'react-redux';
import { selectors } from 'app/services/session/slice';
import { useGetUnitQuery } from 'app/services/me';

type MaintenanceFormProps = {
  handleSubmit: (values: any) => void;
  meta: any;
  maintenance?: ToolMaintenance;
  redirectTo?: string;
  previousFilterSerialNumber?: string;
}

const MaintenanceForm = ({ maintenance, handleSubmit, meta, redirectTo, previousFilterSerialNumber }: MaintenanceFormProps) => {
  const { t } = useTranslation();
  const userName = useSelector(selectors.getCurrentUserName);
  const { data: unit, isLoading: isUnitLoading, isFetching: isUnitFetching } = useGetUnitQuery({ persist: true });
  const [ filterState, setFilterState ] = useState<ToolFilterState | undefined>(maintenance?.FilterState);

  if (!maintenance && (isUnitLoading || isUnitFetching || !unit)) return <Skeleton active />;

  const filterStateOptions = [
    { label: t('entities.maintenance.filterstates.new'), value: ToolFilterState.New },
    { label: t('entities.maintenance.filterstates.old'), value: ToolFilterState.Old },
    { label: t('entities.maintenance.filterstates.none'), value: ToolFilterState.None },
  ];

  const fields: FieldProps[] = [
    {
      name: 'ExecutedAt',
      label: t('entities.maintenance.executedat'),
      type: FieldType.DATE,
      datePicker: { dateFormat: formats.DD_MM_YYYY },
      group: 1,
      required: true,
      mobileGroup: 1
    },
    {
      name: 'IsDryCleaning',
      label: t('entities.maintenance.isdrycleaning'),
      type: FieldType.BOOLEAN,
      group: 2,
      mobileGroup: 2
    },
    {
      name: 'IsWetCleaning',
      label: t('entities.maintenance.iswetcleaning'),
      type: FieldType.BOOLEAN,
      group: 2,
      mobileGroup: 2
    },
    {
      name: 'FilterState',
      label: t('entities.maintenance.filterstate'),
      type: FieldType.SELECT,
      required: true,
      selectProps: {
        options: filterStateOptions,
        labelKey: 'label',
        valueKey: 'value',
        showSearch: false,
        allowClear: false
      },
      onChange: (value: ToolFilterState) => setFilterState(value),
      setFields: {
        keys: [ 'FilterSerialNumber' ],
        onSelect: (value: ToolFilterState) => ({
          FilterSerialNumber: value === ToolFilterState.Old ? (previousFilterSerialNumber || '') : ''
        })
      }
    },
    ...(filterState !== undefined && filterState !== ToolFilterState.None ? [ {
      name: 'FilterSerialNumber',
      label: t('entities.maintenance.filterserialnumber'),
      type: FieldType.TEXT,
      max: 256,
    } ] : []),
    {
      name: 'IsElectricalCheckDone',
      label: t('entities.maintenance.iselectricalcheckdone'),
      type: FieldType.BOOLEAN,
      group: 3,
      mobileGroup: 3
    },
    {
      name: 'IsMechanicalCheckDone',
      label: t('entities.maintenance.ismechanicalcheckdone'),
      type: FieldType.BOOLEAN,
      group: 3,
      mobileGroup: 3
    },
    {
      name: 'IsWorking',
      label: t('entities.maintenance.isworking'),
      type: FieldType.BOOLEAN,
      required: true,
    },
    {
      name: 'Comments',
      label: t('entities.maintenance.comments'),
      type: FieldType.TEXTAREA,
      max: 1024
    },
    {
      name: 'ExecutorName',
      label: t('entities.maintenance.executorname'),
      type: FieldType.TEXT,
      required: true,
      max: 256,
    },
    {
      name: 'CompanyName',
      label: t('entities.maintenance.companyname'),
      type: FieldType.TEXT,
      max: 512,
    },
  ];

  const initData = {
    IsDryCleaning: false,
    IsWetCleaning: false,
    IsElectricalCheckDone: false,
    IsMechanicalCheckDone: false,
    IsWorking: false,
    ExecutorName: userName || '',
    CompanyName: unit?.Name || '',
    ExecutedAt: moment()
  };

  return (
    <DataForm
      id={maintenance ? Drawers.EDIT_MAINTENANCE : Drawers.ADD_MAINTENANCE}
      entity="maintenance"
      onSubmit={handleSubmit}
      fields={fields}
      meta={meta}
      redirectTo={redirectTo}
      initialValues={maintenance ? {
        ...maintenance,
        ExecutedAt: maintenance.ExecutedAt ? moment(maintenance.ExecutedAt) : undefined,
      } : initData}
    />
  );
};

export default MaintenanceForm;

